import type { FC, PropsWithChildren } from 'react'
import React from 'react'
import styled from '@emotion/styled'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import { NamingConventionsIcon, DataIcon, UnderstandingIcon } from '@segment/matcha/components/Icons/Brandbook'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import FeaturesImage from './FeaturesImage'

const copy = {
  heading: `What you’ll get from the Twilio Developer Plan from Segment`,
  cta: {
    text: `Learn more`,
    href: `https://segment.com/docs/guides/usage-and-billing/twilio-developer-plan/`,
  },
  featuresList: [
    {
      icon: <NamingConventionsIcon />,
      title: `Unified customer database`,
      description: `Build a unified view of your customers by loading campaign data from Twilio or Sendgrid and clickstream data from your websites or apps into a data warehouse.`,
    },
    {
      icon: <DataIcon />,
      title: `Query-ready data`,
      description: `Data loaded into your warehouse is automatically optimized for cost and performance, and schematized to make querying simple.`,
    },
    {
      icon: <UnderstandingIcon />,
      title: `Deep customer insights`,
      description: `Leverage your unified customer data set to evaluate campaign performance and deliverability metrics, analyze historical trends, and power data science models. `,
    },
  ],
}

const Features: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <StyledSection variant="2Col">
      <SectionCopy>
        <CopyTextBase>
          <StyledCopyTextHeading variant="h2" tag="h2">
            {copy.heading}
          </StyledCopyTextHeading>
          <ColumnTrack>
            {copy.featuresList.map(({ icon, title, description }, index) => (
              <FeatureBoxComponent key={index} alignment="left" headerPosition="left">
                <FeatureBox.Header>
                  <IconWrapper elevation={1} variant="circle">
                    {icon}
                  </IconWrapper>
                </FeatureBox.Header>
                <FeatureBox.Body>
                  <FeatureBox.Title>{title}</FeatureBox.Title>
                  <FeatureBox.Text>{description}</FeatureBox.Text>
                </FeatureBox.Body>
              </FeatureBoxComponent>
            ))}
          </ColumnTrack>
          <StyledButton href={getAbsoluteUrl(copy.cta.href, pageConfig)}>{copy.cta.text}</StyledButton>
        </CopyTextBase>
      </SectionCopy>

      <Section.Contents>
        <FeaturesImage />
      </Section.Contents>
    </StyledSection>
  )
}

const StyledSection = styled(SectionBase)`
  max-width: 1320px;
`

const StyledCopyTextHeading = styled(CopyText.Heading)`
  margin-bottom: 40px;
`

const SectionCopy = styled(Section.Copy)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 440px;
  }
`

const StyledButton = styled(CopyText.Link)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-left: 70px;
  }
`

const FeatureBoxComponent = styled(FeatureBoxBase)`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    ${FeatureBox.CSSHeaderPositionTop}
  }
`

export default Features
